import { useCallback, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from '@hello-pangea/dnd';
import { DragHandle } from '@mui/icons-material';
import { Box, Button, List, ListItem, Typography } from '@mui/material';
import { useManagePath } from '../../context';
import { getResourceId } from '../../utils/get-resource-id';
import { BaseDialog } from '@/components/shared/base-dialog';
import { useNotification } from '@/context/notification';
import type { ResourceGroup } from '@/data/learning-path/interfaces';

const getResourceTitle = (resource: ResourceGroup): string => {
  if ('course' in resource) return resource.course?.name || '';
  if ('external_link' in resource) return resource.external_link.name;
  return 'Recurso desconhecido';
};

interface ReorderResourcesDialogProps {
  onClose: () => void;
  blockId: string;
}

export const ReorderResourcesDialog = ({
  onClose,
  blockId,
}: ReorderResourcesDialogProps) => {
  const { contents, updateBlock } = useManagePath();
  const { toast } = useNotification();
  const block = contents.find((c) => c.id === blockId);
  const [items, setItems] = useState(
    block?.content && 'resource_groups' in block.content
      ? block.content.resource_groups
      : []
  );

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      const reordered = [...items];
      const [moved] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, moved);
      setItems(reordered);
    },
    [items]
  );

  const handleSave = () => {
    updateBlock(blockId, {
      content: {
        resource_groups: items,
      },
    });

    toast({
      content: 'Recursos reordenados com sucesso!',
      type: 'success',
    });

    onClose();
  };

  if (!block || block.type !== 'resource_group') return null;

  return (
    <BaseDialog
      onClose={onClose}
      open
      fullWidth
      maxWidth="sm"
      title={
        <Typography>
          Reordenar recursos
          <Typography variant="body2" color="text.secondary">
            Altere a ordem dos recursos, arrastando-os.
          </Typography>
        </Typography>
      }
    >
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="resources">
          {(provided) => (
            <List ref={provided.innerRef} {...provided.droppableProps}>
              {items.map((item, index) => (
                <Draggable
                  key={getResourceId(item)}
                  draggableId={getResourceId(item) || ''}
                  index={index}
                >
                  {(providedDraggable, snapshot) => (
                    <ListItem
                      ref={providedDraggable.innerRef}
                      {...providedDraggable.draggableProps}
                      {...providedDraggable.dragHandleProps}
                      secondaryAction={<DragHandle />}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: snapshot.isDragging
                          ? 'action.hover'
                          : 'background.paper',
                        cursor: 'grab',
                      }}
                    >
                      <Typography noWrap>
                        {`${index + 1}. ${getResourceTitle(item)}`}
                      </Typography>
                    </ListItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
      <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
        <Button variant="text" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSave}>
          Salvar
        </Button>
      </Box>
    </BaseDialog>
  );
};
